import { DatePipe } from '@angular/common';
import { Component, Inject, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_FORMATS, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { startWith } from 'rxjs';
import { CourseInstance, Instructor, Resource, Student } from '../../core/models/domain.models';
import { TranslatePipe } from '../../core/i18n/translate.pipe';
import { LocalizationService } from '../../core/i18n/localization.service';
import { EnterSubmitDirective } from '../../core/directives/enter-submit.directive';
import { PL_DASH_DATE_FORMATS, PlDateAdapter } from '../../core/utils/pl-date-adapter';
import { InMemoryDataService } from '../../core/data/in-memory-data.service';

interface AddLessonDialogData {
  students: Student[];
  instructors: Instructor[];
  courseInstances: CourseInstance[];
  resources: Resource[];
  resourcesAlreadyScopedToInstructor?: boolean;
  fixedStudentId?: string | number;
  fixedInstructorId?: string | number;
  hideFixedInstructor?: boolean;
  allowFuturePlanning?: boolean;
  compactCalendarSlot?: boolean;
  initialExecutionDate?: Date;
  initialLessonTime?: string;
  initialDurationHours?: number;
}

@Component({
  selector: 'app-add-lesson-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    EnterSubmitDirective,
    TranslatePipe
    ,DatePipe
  ],
  providers: [
    { provide: DateAdapter, useClass: PlDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: PL_DASH_DATE_FORMATS }
  ],
  template: `
    <h2 mat-dialog-title>{{ 'lessonDialog.title' | t }}</h2>
    <form appEnterSubmit [formGroup]="form" (ngSubmit)="submit()">
    <mat-dialog-content>
      <div class="lesson-form" [class.compact]="data.compactCalendarSlot">
        @if (data.compactCalendarSlot) {
          <div class="slot-summary full-width">
            <span><strong>{{ fixedInstructorLabel() }}</strong></span>
            <span>{{ form.controls.executionDate.value | date:'dd-MM-yyyy' }}</span>
            <span>{{ form.controls.lessonTime.value }}</span>
          </div>
        }
        @if (isStudentFixed()) {
          <mat-form-field>
            <mat-label>{{ 'lessonDialog.student' | t }}</mat-label>
            <input matInput [value]="fixedStudentLabel()" readonly />
          </mat-form-field>
        } @else {
          <mat-form-field>
            <mat-label>{{ 'lessonDialog.student' | t }}</mat-label>
            <input matInput formControlName="studentQuery" [matAutocomplete]="studentAuto" (blur)="onStudentInputBlur()" />
            <mat-autocomplete #studentAuto="matAutocomplete" (optionSelected)="onStudentSelected($event.option.value)">
              @for (student of filteredStudents(); track student.id) {
                <mat-option [value]="student.id">{{ student.name }} {{ student.surname }}</mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>
        }

        @if (!data.compactCalendarSlot && isInstructorFixed() && !data.hideFixedInstructor) {
          <mat-form-field>
            <mat-label>{{ 'lessonDialog.instructor' | t }}</mat-label>
            <input matInput [value]="fixedInstructorLabel()" readonly />
          </mat-form-field>
        }
        @if (!data.compactCalendarSlot && !isInstructorFixed()) {
          <mat-form-field>
            <mat-label>{{ 'lessonDialog.instructor' | t }}</mat-label>
            <input matInput formControlName="instructorQuery" [matAutocomplete]="instructorAuto" (blur)="onInstructorInputBlur()" />
            <mat-autocomplete
              #instructorAuto="matAutocomplete"
              (optionSelected)="onInstructorSelected($event.option.value)"
            >
              @for (instructor of filteredInstructors(); track instructor.id) {
                <mat-option [value]="instructor.id">{{ instructor.name }} {{ instructor.surname }}</mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>
        }

        @if (!data.compactCalendarSlot) {<mat-form-field>
          <mat-label>{{ 'common.date' | t }}</mat-label>
          <input matInput [matDatepicker]="executionDatePicker" formControlName="executionDate" [max]="maxExecutionDate()" />
          <mat-datepicker-toggle matIconSuffix [for]="executionDatePicker"></mat-datepicker-toggle>
          <mat-datepicker #executionDatePicker [touchUi]="isMobilePicker()"></mat-datepicker>
        </mat-form-field>}

        @if (!data.compactCalendarSlot && isMobilePicker()) {
          <div class="lesson-time-picker">
            <mat-form-field>
              <mat-label>{{ 'common.time' | t }} ({{ 'common.hoursShort' | t }})</mat-label>
              <mat-select data-testid="lesson-hour" [value]="form.controls.lessonTime.value?.slice(0, 2)"
                [disabled]="form.controls.lessonTime.disabled" required
                (selectionChange)="setLessonTimePart('hour', $event.value)">
                @for (hour of hourOptions; track hour) {
                  <mat-option [value]="hour">{{ hour }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
            <mat-form-field>
              <mat-label>{{ 'common.time' | t }} ({{ 'common.minutesShort' | t }})</mat-label>
              <mat-select data-testid="lesson-minute" [value]="form.controls.lessonTime.value?.slice(3, 5)"
                [disabled]="form.controls.lessonTime.disabled" required
                (selectionChange)="setLessonTimePart('minute', $event.value)">
                @for (minute of minuteOptions; track minute) {
                  <mat-option [value]="minute">{{ minute }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>
        }
        @if (!data.compactCalendarSlot && !isMobilePicker()) {<mat-form-field>
          <mat-label>{{ 'common.time' | t }}</mat-label>
          <input matInput type="time" formControlName="lessonTime" />
        </mat-form-field>}

        <mat-form-field>
          <mat-label>{{ 'lessonDialog.durationHours' | t }}</mat-label>
          <mat-select formControlName="durationHours">
            @for (item of durationOptions; track item) {
              <mat-option [value]="item">{{ item }}</mat-option>
            }
          </mat-select>
        </mat-form-field>

        @if (!data.compactCalendarSlot || availableCoursesForSelection().length > 1) {<mat-form-field>
          <mat-label>{{ 'lessonDialog.course' | t }}</mat-label>
          <mat-select formControlName="courseInstanceId">
            @for (course of availableCoursesForSelection(); track course.id) {
              <mat-option [value]="course.id">{{ courseLabel(course) }}</mat-option>
            }
          </mat-select>
          @if (form.controls.studentId.value && form.controls.instructorId.value && availableCoursesForSelection().length === 0) {
            <mat-hint class="missing-relation-hint">{{ 'lessonDialog.noSharedCourseHint' | t }}</mat-hint>
          }
        </mat-form-field>}

        @if (!data.compactCalendarSlot || availableResourcesForSelection().length > 1) {<mat-form-field>
          <mat-label>{{ 'lessonDialog.resource' | t }}</mat-label>
          <mat-select formControlName="resourceId">
            @for (resource of availableResourcesForSelection(); track resource.id) {
              <mat-option [value]="resource.id">{{ resource.name }}</mat-option>
            }
          </mat-select>
          @if (form.controls.courseInstanceId.value && availableResourcesForSelection().length === 0) {
            <mat-hint class="missing-relation-hint">{{ 'lessonDialog.noAssignedResourceHint' | t }}</mat-hint>
          }
        </mat-form-field>}

        <mat-form-field class="full-width">
          <mat-label>{{ 'lessonDialog.commentForStudent' | t }}</mat-label>
          <textarea matInput rows="2" formControlName="studentComment"></textarea>
        </mat-form-field>

        <mat-form-field class="full-width">
          <mat-label>{{ 'lessonDialog.privateNote' | t }}</mat-label>
          <textarea matInput rows="2" formControlName="instructorPrivateNote"></textarea>
        </mat-form-field>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" [disabled]="submitting()" (click)="dialogRef.close(null)">{{ 'common.cancel' | t }}</button>
      <button
        mat-flat-button
        type="submit"
        color="primary"
        [disabled]="form.invalid || submitting() || availableCoursesForSelection().length === 0 || availableResourcesForSelection().length === 0"
      >{{ 'lessonDialog.addLesson' | t }}</button>
    </mat-dialog-actions>
    </form>
  `,
  styles: `
    form { display: contents; }
    .lesson-form {
      display: grid;
      grid-template-columns: repeat(2, minmax(300px, 1fr));
      gap: 14px;
      min-width: 860px;
    }
    .lesson-form mat-form-field {
      width: 100%;
    }
    .lesson-form .full-width {
      grid-column: 1 / -1;
    }
    .missing-relation-hint { color: #b54708; font-weight: 600; }
    .lesson-time-picker { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
    .lesson-form.compact { min-width:0;grid-template-columns:1fr; }
    .slot-summary { display:flex;flex-wrap:wrap;gap:8px 18px;padding:12px 14px;border:1px solid #c9d9ee;border-radius:8px;background:#f1f7ff;color:#173a64; }
    @media (max-width: 760px) {
      .lesson-form {
        grid-template-columns: 1fr;
        min-width: 280px;
      }
    }
  `
})
export class AddLessonDialogComponent {
  private readonly fb = inject(FormBuilder);
  private readonly dataService = inject(InMemoryDataService);
  private readonly localization = inject(LocalizationService);
  readonly submitting = signal(false);
  private readonly defaultExecutionDate = this.todayDate();
  private readonly defaultLessonTime = this.previousFullHour();
  readonly maxExecutionDate = computed<Date | null>(() => (this.data.allowFuturePlanning === false ? this.todayDate() : null));

  readonly durationOptions = [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5];
  readonly hourOptions = Array.from({ length: 24 }, (_, hour) => String(hour).padStart(2, '0'));
  readonly minuteOptions = ['00', '15', '30', '45'];
  readonly isStudentFixed = computed(() => this.hasValue(this.data.fixedStudentId));
  readonly isInstructorFixed = computed(() => this.hasValue(this.data.fixedInstructorId));
  readonly fixedStudentLabel = computed(() => {
    if (!this.isStudentFixed()) {
      return '';
    }
    const student = this.data.students.find((item) => this.sameId(item.id, this.data.fixedStudentId));
    return student ? `${student.name} ${student.surname}` : '';
  });
  readonly fixedInstructorLabel = computed(() => {
    if (!this.isInstructorFixed()) {
      return '';
    }
    const instructor = this.data.instructors.find((item) => this.sameId(item.id, this.data.fixedInstructorId));
    return instructor ? `${instructor.name} ${instructor.surname}` : '';
  });

  readonly form = this.fb.group({
    studentQuery: ['', Validators.required],
    studentId: ['' as string | number, Validators.required],
    instructorQuery: ['', Validators.required],
    instructorId: ['' as string | number, Validators.required],
    executionDate: [this.defaultExecutionDate as Date | null, Validators.required],
    lessonTime: [this.defaultLessonTime, [Validators.required, Validators.pattern(/^([01]\d|2[0-3]):[0-5]\d$/)]],
    durationHours: [1, Validators.required],
    courseInstanceId: ['' as string | number, Validators.required],
    resourceId: ['' as string | number, Validators.required],
    studentComment: [''],
    instructorPrivateNote: ['']
  });

  private readonly studentQuerySignal = toSignal(this.form.controls.studentQuery.valueChanges.pipe(startWith('')), {
    initialValue: ''
  });
  private readonly studentIdSignal = toSignal(this.form.controls.studentId.valueChanges.pipe(startWith('')), {
    initialValue: ''
  });
  private readonly instructorIdSignal = toSignal(this.form.controls.instructorId.valueChanges.pipe(startWith('')), {
    initialValue: ''
  });
  private readonly courseInstanceIdSignal = toSignal(this.form.controls.courseInstanceId.valueChanges.pipe(startWith('')), {
    initialValue: ''
  });
  private readonly instructorQuerySignal = toSignal(
    this.form.controls.instructorQuery.valueChanges.pipe(startWith('')),
    { initialValue: '' }
  );

  readonly filteredStudents = computed(() => {
    const query = `${this.studentQuerySignal() ?? ''}`.toLowerCase().trim();
    if (!query || query === this.selectedStudentLabel().toLowerCase()) {
      return this.data.students;
    }
    return this.data.students.filter((item) => `${item.name} ${item.surname}`.toLowerCase().includes(query));
  });

  readonly filteredInstructors = computed(() => {
    const query = `${this.instructorQuerySignal() ?? ''}`.toLowerCase().trim();
    if (!query || query === this.selectedInstructorLabel().toLowerCase()) {
      return this.data.instructors;
    }
    return this.data.instructors.filter((item) => `${item.name} ${item.surname}`.toLowerCase().includes(query));
  });

  readonly availableCoursesForSelection = computed(() => {
    const selectedStudentId = this.studentIdSignal();
    const selectedInstructorId = this.instructorIdSignal();
    const selectedStudent = this.data.students.find((item) => this.sameId(item.id, selectedStudentId));
    const selectedInstructor = this.data.instructors.find((item) => this.sameId(item.id, selectedInstructorId));
    if (!selectedStudent || !selectedInstructor) {
      return [];
    }

    const studentCourseIds = this.studentCourseIds(selectedStudent);
    const instructorCourseIds = new Set(selectedInstructor.courseInstanceIds ?? []);
    const scopedFixedInstructor = this.isScopedFixedInstructor(selectedInstructor);

    return this.data.courseInstances.filter((item) => {
      const linkedToStudent =
        this.hasId(studentCourseIds, item.id) || item.studentIds.some((studentId) => this.sameId(studentId, selectedStudent.id));
      const linkedToInstructor =
        scopedFixedInstructor ||
        this.hasId(instructorCourseIds, item.id) ||
        item.instructorIds.some((instructorId) => this.sameId(instructorId, selectedInstructor.id));
      return (linkedToStudent || item.courseType === 'AD_HOC') && linkedToInstructor && item.status === 'active';
    });
  });

  readonly availableResourcesForSelection = computed(() => {
    const selectedInstructorId = this.instructorIdSignal();
    const selectedCourseInstanceId = this.courseInstanceIdSignal();
    const selectedInstructor = this.data.instructors.find((item) => this.sameId(item.id, selectedInstructorId));
    const selectedCourse = this.data.courseInstances.find((item) => this.sameId(item.id, selectedCourseInstanceId));
    if (!selectedInstructor) {
      return [];
    }

    const instructorResourceIds = selectedInstructor.resourceIds ?? [];

    return this.data.resources.filter((resource) => {
      if (!this.data.resourcesAlreadyScopedToInstructor && !instructorResourceIds.some((id) => this.sameId(id, resource.id))) {
        return false;
      }
      const linkedToCourse = !selectedCourseInstanceId || resource.courseInstanceIds.some((id) => this.sameId(id, selectedCourseInstanceId));
      const courseCategories = selectedCourse ? this.resolveCourseCategories(selectedCourse) : [];
      const resourceCategories = resource.categories?.length ? resource.categories : [resource.category].filter(Boolean);
      const categoryMatches = courseCategories.length === 0 || courseCategories.some((category) => resourceCategories.includes(category));
      return linkedToCourse && categoryMatches;
    });
  });

  courseLabel(course: CourseInstance): string {
    if (course.courseType !== 'AD_HOC') return course.name;
    const category = this.resolveCourseCategories(course)[0] ?? '';
    const categoryLabel = category ? this.localization.translate('common.category') + ' ' + category : '';
    return [this.localization.translate('courseType.adHocShort'), categoryLabel].filter(Boolean).join(' · ');
  }

  constructor(
    readonly dialogRef: MatDialogRef<AddLessonDialogComponent, Record<string, unknown> | null>,
    @Inject(MAT_DIALOG_DATA) readonly data: AddLessonDialogData
  ) {
    this.form.controls.courseInstanceId.valueChanges.subscribe(() => this.syncSelectedResource());
    this.form.controls.studentId.valueChanges.subscribe(() => this.syncRelationControls());
    this.form.controls.instructorId.valueChanges.subscribe(() => this.syncRelationControls());
    this.form.controls.courseInstanceId.valueChanges.subscribe(() => this.syncRelationControls());
    this.form.controls.studentQuery.valueChanges.subscribe((value) => this.resetStudentSelectionIfQueryChanged(value));
    this.form.controls.instructorQuery.valueChanges.subscribe((value) => this.resetInstructorSelectionIfQueryChanged(value));

    const fixedStudentId = this.data.fixedStudentId;
    if (this.hasValue(fixedStudentId)) {
      this.onStudentSelected(fixedStudentId);
      this.form.controls.studentQuery.disable({ emitEvent: false });
    }

    const fixedInstructorId = this.data.fixedInstructorId;
    if (this.hasValue(fixedInstructorId)) {
      this.onInstructorSelected(fixedInstructorId);
      this.form.controls.instructorQuery.disable({ emitEvent: false });
    }

    if (this.data.students.length === 1) {
      this.onStudentSelected(this.data.students[0].id);
    }

    if (this.data.compactCalendarSlot) {
      this.form.patchValue({
        executionDate: this.data.initialExecutionDate ?? this.defaultExecutionDate,
        lessonTime: this.data.initialLessonTime ?? this.defaultLessonTime,
        durationHours: this.data.initialDurationHours ?? 1
      });
    }

    this.syncSelectedResource();
    this.syncRelationControls();
  }

  private syncRelationControls(): void {
    this.setControlDisabled(this.form.controls.courseInstanceId, this.availableCoursesForSelection().length === 0);
    this.setControlDisabled(this.form.controls.resourceId, this.availableResourcesForSelection().length === 0);
  }

  private setControlDisabled(control: { disabled: boolean; disable(options: { emitEvent: boolean }): void; enable(options: { emitEvent: boolean }): void }, disabled: boolean): void {
    if (disabled && !control.disabled) control.disable({ emitEvent: false });
    if (!disabled && control.disabled) control.enable({ emitEvent: false });
  }

  private selectedStudentLabel(): string {
    const selected = this.data.students.find((item) => this.sameId(item.id, this.form.controls.studentId.value));
    return selected ? `${selected.name} ${selected.surname}`.trim() : '';
  }

  private selectedInstructorLabel(): string {
    const selected = this.data.instructors.find((item) => this.sameId(item.id, this.form.controls.instructorId.value));
    return selected ? `${selected.name} ${selected.surname}`.trim() : '';
  }

  private resetStudentSelectionIfQueryChanged(value: unknown): void {
    if (this.isStudentFixed()) {
      return;
    }
    const selectedLabel = this.selectedStudentLabel();
    if (!selectedLabel) {
      return;
    }
    if (`${value ?? ''}`.trim() === selectedLabel) {
      return;
    }
    this.form.patchValue({ studentId: '', courseInstanceId: '', resourceId: '' });
  }

  private resetInstructorSelectionIfQueryChanged(value: unknown): void {
    if (this.isInstructorFixed()) {
      return;
    }
    const selectedLabel = this.selectedInstructorLabel();
    if (!selectedLabel) {
      return;
    }
    if (`${value ?? ''}`.trim() === selectedLabel) {
      return;
    }
    this.form.patchValue({ instructorId: '', courseInstanceId: '', resourceId: '' });
  }

  private resolveCourseCategories(courseInstance: CourseInstance | undefined): string[] {
    if (!courseInstance) {
      return [];
    }
    if (Array.isArray(courseInstance.categories) && courseInstance.categories.length > 0) {
      return courseInstance.categories;
    }
    if (courseInstance.category) {
      return [courseInstance.category];
    }
    return [];
  }

  onStudentSelected(studentId: string | number): void {
    const selected = this.data.students.find((item) => this.sameId(item.id, studentId));
    if (!selected) {
      return;
    }
    this.form.patchValue({
      studentId,
      studentQuery: `${selected.name} ${selected.surname}`,
      courseInstanceId: '',
      resourceId: ''
    });
    const availableCourses = this.availableCoursesForSelection();
    if (availableCourses.length === 1) {
      this.form.patchValue({ courseInstanceId: availableCourses[0].id });
    }
    this.syncSelectedResource();
  }

  onStudentInputBlur(): void {
    if (this.isStudentFixed()) {
      return;
    }

    const rawQuery = `${this.form.controls.studentQuery.value ?? ''}`.trim().toLowerCase();
    if (!rawQuery) {
      this.form.patchValue({
        studentId: '',
        courseInstanceId: '',
        resourceId: ''
      });
      return;
    }

    const exact = this.data.students.find((item) => `${item.name} ${item.surname}`.trim().toLowerCase() === rawQuery);
    const partialMatches = this.data.students.filter((item) =>
      `${item.name} ${item.surname}`.trim().toLowerCase().includes(rawQuery)
    );
    const matched = exact ?? (partialMatches.length === 1 ? partialMatches[0] : undefined);

    if (!matched) {
      this.form.patchValue({
        studentId: '',
        courseInstanceId: '',
        resourceId: ''
      });
      return;
    }

    this.form.patchValue({
      studentId: matched.id,
      studentQuery: `${matched.name} ${matched.surname}`,
      courseInstanceId: '',
      resourceId: ''
    });
    const availableCourses = this.availableCoursesForSelection();
    if (availableCourses.length === 1) {
      this.form.patchValue({ courseInstanceId: availableCourses[0].id });
    }
    this.syncSelectedResource();
  }

  onInstructorSelected(instructorId: string | number): void {
    const selected = this.data.instructors.find((item) => this.sameId(item.id, instructorId));
    if (!selected) {
      return;
    }
    this.form.patchValue({
      instructorId,
      instructorQuery: `${selected.name} ${selected.surname}`,
      courseInstanceId: '',
      resourceId: ''
    });
    const availableCourses = this.availableCoursesForSelection();
    if (availableCourses.length === 1) {
      this.form.patchValue({ courseInstanceId: availableCourses[0].id });
    }
    this.syncSelectedResource();
  }

  onInstructorInputBlur(): void {
    if (this.isInstructorFixed()) {
      return;
    }

    const rawQuery = `${this.form.controls.instructorQuery.value ?? ''}`.trim().toLowerCase();
    if (!rawQuery) {
      this.form.patchValue({
        instructorId: '',
        courseInstanceId: '',
        resourceId: ''
      });
      return;
    }

    const exact = this.data.instructors.find((item) => `${item.name} ${item.surname}`.trim().toLowerCase() === rawQuery);
    const partialMatches = this.data.instructors.filter((item) =>
      `${item.name} ${item.surname}`.trim().toLowerCase().includes(rawQuery)
    );
    const matched = exact ?? (partialMatches.length === 1 ? partialMatches[0] : undefined);

    if (!matched) {
      this.form.patchValue({
        instructorId: '',
        courseInstanceId: '',
        resourceId: ''
      });
      return;
    }

    this.form.patchValue({
      instructorId: matched.id,
      instructorQuery: `${matched.name} ${matched.surname}`,
      courseInstanceId: '',
      resourceId: ''
    });
    const availableCourses = this.availableCoursesForSelection();
    if (availableCourses.length === 1) {
      this.form.patchValue({ courseInstanceId: availableCourses[0].id });
    }
    this.syncSelectedResource();
  }

  private syncSelectedResource(): void {
    const availableResources = this.availableResourcesForSelection();
    const currentResourceId = this.form.controls.resourceId.value;
    const currentStillAvailable = availableResources.some((item) => this.sameId(item.id, currentResourceId));
    if (currentStillAvailable) {
      return;
    }

    const selectedInstructor = this.data.instructors.find((item) =>
      this.sameId(item.id, this.form.controls.instructorId.value)
    );
    const defaultResource = selectedInstructor?.defaultResourceId == null
      ? undefined
      : availableResources.find((item) => this.sameId(item.id, selectedInstructor.defaultResourceId));
    if (defaultResource) {
      this.form.patchValue({ resourceId: defaultResource.id });
      return;
    }

    this.form.patchValue({ resourceId: availableResources.length === 1 ? availableResources[0].id : '' });
  }

  async submit(): Promise<void> {
    if (this.submitting()) {
      return;
    }
    if (this.form.invalid || this.availableCoursesForSelection().length === 0 || this.availableResourcesForSelection().length === 0) {
      this.form.markAllAsTouched();
      return;
    }
    const value = this.form.getRawValue();
    const executionDate = this.toIsoDate(value.executionDate);
    if (!executionDate) {
      this.form.controls.executionDate.setErrors({ invalidDate: true });
      this.form.controls.executionDate.markAsTouched();
      return;
    }
    if (this.data.allowFuturePlanning === false && executionDate > this.todayIso()) {
      this.form.controls.executionDate.setErrors({ futureDateBlocked: true });
      this.form.controls.executionDate.markAsTouched();
      return;
    }
    const payload = {
      studentId: value.studentId,
      instructorId: value.instructorId,
      courseInstanceId: value.courseInstanceId,
      resourceId: value.resourceId,
      executionDate,
      lessonTime: value.lessonTime,
      durationMinutes: Number(value.durationHours) * 60,
      studentComment: value.studentComment,
      instructorPrivateNote: value.instructorPrivateNote,
      additionalInformation: value.studentComment
    };

    this.submitting.set(true);
    this.form.disable({ emitEvent: false });
    try {
      await this.dataService.createDynamicFromBackend('lessons', payload);
      this.dialogRef.close({ created: true });
    } catch {
      this.form.enable({ emitEvent: false });
      if (this.isStudentFixed()) {
        this.form.controls.studentQuery.disable({ emitEvent: false });
      }
      if (this.isInstructorFixed()) {
        this.form.controls.instructorQuery.disable({ emitEvent: false });
      }
    } finally {
      this.submitting.set(false);
    }
  }

  isMobilePicker(): boolean {
    return typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches;
  }

  private toIsoDate(value: unknown): string {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
    }
    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }
    return '';
  }

  private todayDate(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }

  private todayIso(): string {
    return new Date().toISOString().slice(0, 10);
  }

  private previousFullHour(): string {
    const now = new Date();
    const previousHour = Math.max(now.getHours() - 1, 0);
    return `${String(previousHour).padStart(2, '0')}:00`;
  }

  private sameId(left: unknown, right: unknown): boolean {
    return `${left ?? ''}` === `${right ?? ''}`;
  }

  private hasValue(value: unknown): value is string | number {
    return value != null && `${value}`.trim().length > 0;
  }

  private isScopedFixedInstructor(instructor: Instructor): boolean {
    return this.isInstructorFixed() && this.data.instructors.length === 1 && (instructor.courseInstanceIds ?? []).length === 0;
  }

  setLessonTimePart(part: 'hour' | 'minute', value: string): void {
    const control = this.form.controls.lessonTime;
    if (control.disabled) return;
    const [hour, minute] = (control.value ?? this.defaultLessonTime).split(':');
    control.setValue(part === 'hour' ? `${value}:${minute}` : `${hour}:${value}`);
    control.markAsDirty();
    control.markAsTouched();
  }

  private studentCourseIds(student: Student): Set<unknown> {
    const ids = new Set<unknown>(student.courseInstanceIds ?? []);
    const instances = Array.isArray(student.studentCourseInstances) ? student.studentCourseInstances : [];
    for (const instance of instances) {
      ids.add(instance['courseInstanceId'] ?? instance['courseId'] ?? instance['id']);
    }
    return ids;
  }

  private hasId(values: Set<unknown>, id: unknown): boolean {
    return Array.from(values).some((value) => this.sameId(value, id));
  }
}
